<template>
  <div class="cms-faq-form">
    <cms-form-header
      :title="isEditing ? $t('cms.faq.editFaq') : $t('cms.faq.createFaq')"
      :disabled="isSaving || isLoadingFaq"
      @back="goBack"
      @save-draft="saveDraft"
      @save-publish="saveAndPublish"
    />

    <cms-form-layout>
      <form @submit.prevent="handleSubmit">
        <cms-form-section :title="$t('cms.faq.generalInfo')">
          <div class="cms-form-grid">
            <cms-select
              v-model="categorySelectValue"
              :label="$t('cms.faq.category')"
              :options="categoryOptions"
              :placeholder="$t('cms.faq.selectCategory')"
              :full-width="false"
              :disabled="isEditing || !!route.query.category_id"
            />
          </div>
        </cms-form-section>

        <cms-form-section :title="$t('cms.faq.contentSection')">
          <cms-translation-tabs :languages="languages" v-model="currentLanguage">
            <template #default="{ locale }">
              <div class="cms-form-grid">
                <cms-input
                  v-model="getTranslation(locale).question"
                  :label="`${$t('cms.faq.question')} (${locale.toUpperCase()})`"
                  :full-width="true"
                />
                <cms-editor
                  v-model="getTranslation(locale).answer"
                  :label="`${$t('cms.faq.answer')} (${locale.toUpperCase()})`"
                />
              </div>
            </template>
          </cms-translation-tabs>
        </cms-form-section>
      </form>
    </cms-form-layout>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useAppLanguages } from '@/composables/useAppLanguages';
import {
  CmsFormHeader,
  CmsFormLayout,
  CmsFormSection,
  CmsInput,
  CmsEditor,
  CmsSelect,
  CmsTranslationTabs,
} from '@/components/cms';
import { normalizeResource, extractRelationship } from '@/composables/useCmsActivityData';

interface FaqTranslation {
  locale: string;
  question: string;
  answer: string;
}

interface FaqForm {
  cms_faq_category_id: number | null;
  order: number;
  is_public: boolean;
  translations: FaqTranslation[];
}

export default defineComponent({
  name: 'TheCmsFaqForm',
  components: {
    CmsFormHeader,
    CmsFormLayout,
    CmsFormSection,
    CmsInput,
    CmsEditor,
    CmsSelect,
    CmsTranslationTabs,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const toast = useToast();
    const authStore = useAuthStore();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const faqId = ref(route.params.id ? Number(route.params.id) : null);
    const isEditing = computed(() => faqId.value !== null);
    const isSaving = ref(false);
    const isLoadingFaq = ref(false);
    const categoryOptions = ref<Array<{ value: string | number; label: string }>>([]);

    const { languages, locales, resolveStoredLocale } = useAppLanguages();
    const currentLanguage = ref(resolveStoredLocale(localStorage.getItem('language')));

    const createEmptyTranslation = (locale: string): FaqTranslation => ({
      locale,
      question: '',
      answer: '',
    });

    const form = reactive<FaqForm>({
      cms_faq_category_id: route.query.category_id ? Number(route.query.category_id) : null,
      order: 0,
      is_public: false,
      translations: locales.value.map((locale) => createEmptyTranslation(locale)),
    });

    const getTranslation = (locale: string): FaqTranslation => {
      let translation = form.translations.find((t) => t.locale === locale);
      if (!translation) {
        translation = createEmptyTranslation(locale);
        form.translations.push(translation);
      }
      return translation;
    };

    const categorySelectValue = computed({
      get: () => {
        // Return as string to match option values
        return form.cms_faq_category_id !== null ? String(form.cms_faq_category_id) : '';
      },
      set: (value: number | string) => {
        if (value === '' || value === null) {
          form.cms_faq_category_id = null;
          return;
        }
        // Convert string back to number for form storage
        const parsed = typeof value === 'number' ? value : Number(value);
        form.cms_faq_category_id = Number.isNaN(parsed) ? null : parsed;
      },
    });

    const loadCategories = async () => {
      if (!agencySlug.value) return;

      try {
        const response = await api.faqCategoryService.listCmsFaqCategories(agencySlug.value, {
          perPage: 0,
        });

        const extractListData = (response: any): any[] => {
          if (!response) return [];
          const candidates = [
            response?.data?.data,
            response?.data,
            response?.data?.data?.data,
            response,
          ];
          for (const candidate of candidates) {
            if (Array.isArray(candidate)) {
              return candidate;
            }
          }
          return [];
        };

        const list = extractListData(response);
        const locale = currentLanguage.value;

        categoryOptions.value = list.map((category: any) => {
          const normalized = normalizeResource(category) || category;
          const translations = normalizeResource(extractRelationship(category, 'translations')) || [];
          const translation = translations.find((t: any) => t.locale === locale) || translations[0] || {};

          return {
            value: String(normalized.id),
            label: translation.name || '',
          };
        });
      } catch (error) {
        console.error('Failed to load categories', error);
      }
    };

    const loadFaq = async () => {
      if (!agencySlug.value || !faqId.value) {
        return;
      }
      isLoadingFaq.value = true;
      try {
        const response = await api.faqService.getCmsFaq(agencySlug.value, faqId.value);
        const faq = normalizeResource(response) || response;
        
        form.cms_faq_category_id = faq.cmsFaqCategoryId;
        form.order = faq.order || 0;
        form.is_public = faq.is_public || false;

        const translations = normalizeResource(extractRelationship(response, 'translations')) || [];
        form.translations = locales.value.map((locale) => {
          const translation = translations.find((t: any) => t.locale === locale) || {};
          return {
            locale,
            question: translation.question || '',
            answer: translation.answer || '',
          };
        });
      } catch (error) {
        console.error('Failed to load FAQ', error);
        toast.error(t('general.errorMessage'));
      } finally {
        isLoadingFaq.value = false;
      }
    };

    const buildPayload = () => {
      return {
        cms_faq_category_id: form.cms_faq_category_id,
        order: form.order,
        is_public: form.is_public,
        translations: form.translations
          .filter((t) => t.question.trim() && t.answer.trim())
          .map((t) => ({
            locale: t.locale,
            question: t.question.trim(),
            answer: t.answer.trim(),
          })),
      };
    };

    const saveDraft = async () => {
      if (!agencySlug.value) return;

      form.is_public = false;
      await persistFaq(false);
    };

    const saveAndPublish = async () => {
      if (!agencySlug.value) return;

      form.is_public = true;
      await persistFaq(true);
    };

    const persistFaq = async (publish = false) => {
      if (!agencySlug.value) return;

      isSaving.value = true;
      try {
        const payload = buildPayload();

        if (isEditing.value && faqId.value) {
          await api.faqService.updateCmsFaq(agencySlug.value, faqId.value, payload);
          if (publish) {
            await api.faqService.publishCmsFaq(agencySlug.value, faqId.value, { is_public: true });
          }
          toast.success(publish ? t('cms.faq.publishSuccess') : t('general.shared.savedChanges'));
          goBack();
        } else {
          const response = await api.faqService.createCmsFaq(agencySlug.value, payload);
          const newId = response?.id || response?.data?.id || response?.data?.data?.id;
          if (newId) {
            faqId.value = newId;
            if (publish) {
              await api.faqService.publishCmsFaq(agencySlug.value, newId, { is_public: true });
            }
            toast.success(publish ? t('cms.faq.publishSuccess') : t('general.shared.savedChanges'));
            goBack();
          }
        }
      } catch (error: any) {
        console.error('Failed to save FAQ', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        isSaving.value = false;
      }
    };

    const handleSubmit = () => {
      saveAndPublish();
    };

    const goBack = () => {
      if (form.cms_faq_category_id) {
        router.push({ name: 'cms.help-center.faqs', params: { categoryId: form.cms_faq_category_id } });
      } else {
        router.push({ name: 'cms.help-center' });
      }
    };

    watch(
      () => route.params.id,
      async (newId) => {
        if (newId) {
          faqId.value = Number(newId);
          // Ensure categories are loaded before loading FAQ
          if (categoryOptions.value.length === 0) {
            await loadCategories();
          }
          await loadFaq();
        }
      },
      { immediate: true },
    );

    // Watch for when categories are loaded to ensure the selected value is properly set
    watch(
      () => [categoryOptions.value, form.cms_faq_category_id],
      () => {
        // When categories are loaded and we have a category ID, ensure it's valid
        if (categoryOptions.value.length > 0 && form.cms_faq_category_id !== null) {
          const categoryIdString = String(form.cms_faq_category_id);
          const categoryExists = categoryOptions.value.some(
            (opt) => String(opt.value) === categoryIdString,
          );
          if (!categoryExists) {
            // Category ID doesn't exist in options, might be a timing issue
            // Try to reload categories or log a warning
            console.warn('Category ID not found in options:', form.cms_faq_category_id);
          }
        }
      },
      { deep: true },
    );

    onMounted(async () => {
      await loadCategories();
      if (isEditing.value && !faqId.value) {
        // Only load FAQ if it wasn't already loaded by the watch
        await loadFaq();
      }
    });

    return {
      isEditing,
      isSaving,
      isLoadingFaq,
      form,
      categoryOptions,
      categorySelectValue,
      languages,
      currentLanguage,
      getTranslation,
      handleSubmit,
      saveDraft,
      saveAndPublish,
      goBack,
      route,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

.cms-faq-form {
  color: $cms-text-primary;
}
</style>

